// src/pages/CreateEventPage.js - Inline Create Event Page
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { eventService } from '../services/api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import PeopleIcon from '@mui/icons-material/People';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import PersonIcon from '@mui/icons-material/Person';

const CreateEventPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    brideName: '',
    groomName: '',
    startDate: '',
    endDate: '',
    venue: '',
    budget: '',
    pointOfContactName: '',
    pointOfContactNumber: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Parse Indian currency formats like "1cr", "1 crore", "1,50,000", etc.
  const parseBudget = (budgetStr) => {
    if (!budgetStr || budgetStr.trim() === '') return undefined;
    
    // Remove commas and convert to lowercase
    const cleanStr = budgetStr.replace(/,/g, '').toLowerCase().trim();
    
    // Handle crore variations
    if (cleanStr.includes('cr') || cleanStr.includes('crore')) {
      const number = parseFloat(cleanStr.replace(/[^\d.]/g, ''));
      return number * 10000000; // 1 crore = 10,000,000
    }
    
    // Handle lakh variations
    if (cleanStr.includes('l') || cleanStr.includes('lakh')) {
      const number = parseFloat(cleanStr.replace(/[^\d.]/g, ''));
      return number * 100000; // 1 lakh = 100,000
    }
    
    // Handle thousand variations
    if (cleanStr.includes('k') || cleanStr.includes('thousand')) {
      const number = parseFloat(cleanStr.replace(/[^\d.]/g, ''));
      return number * 1000;
    }
    
    // Handle regular numbers
    const number = parseFloat(cleanStr.replace(/[^\d.]/g, ''));
    return isNaN(number) ? undefined : number;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Form validation
    if (!formData.name.trim()) {
      setError('Event name is required');
      setLoading(false);
      return;
    }

    if (!formData.startDate) {
      setError('Event start date is required');
      setLoading(false);
      return;
    }

    if (!formData.endDate) {
      setError('Event end date is required');
      setLoading(false);
      return;
    }

    if (new Date(formData.startDate) > new Date(formData.endDate)) {
      setError('Start date must be before end date');
      setLoading(false);
      return;
    }

    try {
      const eventData = {
        name: formData.name,
        brideName: formData.brideName,
        groomName: formData.groomName,
        startDate: formData.startDate,
        endDate: formData.endDate,
        venue: formData.venue,
        budget: parseBudget(formData.budget),
        pointOfContactName: formData.pointOfContactName,
        pointOfContactNumber: formData.pointOfContactNumber
      };

      const newEvent = await eventService.createEvent(eventData);
      console.log('Event created:', newEvent);
      
      // Navigate to the new event's details page
      navigate(`/event/${newEvent._id}`);
    } catch (error) {
      console.error('Error creating event:', error);
      const code = error.response?.data?.code;
      if (code === 'TIER_LIMIT_EVENTS' || code === 'NO_SUBSCRIPTION') {
        setError(error.response.data.message);
      } else {
        setError('Failed to create event. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderFormField = (field, label, icon, placeholder = '', type = 'text', minDate = null) => {
    return (
      <div className="form-field">
        <label className="form-label">
          {icon}
          <span>{label}</span>
        </label>
        <div className="form-input-container">
          <input
            type={type}
            value={formData[field]}
            onChange={(e) => handleInputChange(field, e.target.value)}
            placeholder={placeholder}
            className="form-input"
            required={field === 'name' || field === 'startDate' || field === 'endDate'}
            min={type === 'date' && minDate ? minDate : undefined}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="event-overview-container">
      <div className="overview-header">
        <div className="header-left">
          <button 
            className="back-button"
            onClick={() => navigate('/')}
            title="Back to Events"
          >
            <ArrowBackIcon />
          </button>
          <div>
            <h3>New event</h3>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="event-form">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {renderFormField(
          'name',
          'Event name',
          <EditIcon className="form-icon" />,
          'Enter event name'
        )}

        {renderFormField(
          'brideName',
          'Bride name',
          <PersonIcon className="form-icon" />,
          'Enter bride name'
        )}

        {renderFormField(
          'groomName',
          'Groom name',
          <PersonIcon className="form-icon" />,
          'Enter groom name'
        )}

        {renderFormField(
          'startDate',
          'Start Date',
          <CalendarTodayIcon className="form-icon" />,
          'Select start date',
          'date'
        )}

        {renderFormField(
          'endDate',
          'End Date',
          <CalendarTodayIcon className="form-icon" />,
          'Select end date',
          'date',
          formData.startDate // Pass startDate as minimum date
        )}

        {renderFormField(
          'venue',
          'Location',
          <LocationOnIcon className="form-icon" />,
          'Enter venue location'
        )}

        {renderFormField(
          'budget',
          'Budget (₹)',
          <CurrencyRupeeIcon className="form-icon" />,
          'e.g., 5,00,000 or 5L or 1cr',
          'text'
        )}

        {renderFormField(
          'pointOfContactName',
          'Point of contact',
          <PersonIcon className="form-icon" />,
          'Enter contact person name'
        )}

        {renderFormField(
          'pointOfContactNumber',
          'Contact number',
          <ContactPhoneIcon className="form-icon" />,
          'Enter contact phone number',
          'tel'
        )}

        <div className="form-actions">
          <button 
            type="submit" 
            className="save-event-btn"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Save event'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateEventPage;